import PusherSDK from "pusher-js";
import type { Channel } from "pusher-js";
import type { BroadcastingService } from "../types";

export class ClientService implements BroadcastingService {
  pusher: PusherSDK | undefined;

  constructor(key: string, cluster: string) {
    this.startConnection(key, cluster);

    this.listenEvents = this.listenEvents.bind(this);
    this.unlistenEvents = this.unlistenEvents.bind(this);
    this.channelNameTransformer = this.channelNameTransformer.bind(this);
    this.getChannel = this.getChannel.bind(this);
    this.isSubscribed = this.isSubscribed.bind(this);
    this.subscribe = this.subscribe.bind(this);
    this.assertPusher = this.assertPusher.bind(this);
    this.startConnection = this.startConnection.bind(this);
  }

  public async listenEvents(
    channelName: string,
    events: string[] | "*",
    callback: (payload: unknown) => void,
  ) {
    const _channelName = this.channelNameTransformer(channelName);

    if (!this.isSubscribed(_channelName)) {
      await this.subscribe(_channelName);
    }

    const channel = this.getChannel(_channelName);

    if (events === "*") {
      channel.bind_global(callback);
      return;
    }

    events.forEach((event: string): void => {
      channel.bind(event, callback);
    });
  }

  public unlistenEvents(
    channelName: string,
    events: string[] | "*",
    callback: (payload: unknown) => void,
  ): void {
    const _channelName = this.channelNameTransformer(channelName);

    if (!this.isSubscribed(_channelName)) {
      return;
    }

    const channel = this.getChannel(_channelName);

    if (events === "*") {
      channel.unbind_global(callback);
      channel.unsubscribe();
      return;
    }

    events.forEach((event: string): void => {
      channel.unbind(event, callback);
    });
  }

  private startConnection(key: string, cluster: string) {
    this.pusher = new PusherSDK(key, {
      cluster,
      forceTLS: process.env.NODE_ENV !== "development",
    });
  }

  private assertPusher(p: typeof this.pusher): asserts p {
    if (!p) {
      throw new Error("Pusher connection has not started yet.");
    }
  }

  private getChannel(name: string): Channel {
    this.assertPusher(this.pusher);

    const channel = this.pusher.channels.channels?.[name];

    if (!channel) {
      throw new Error(
        `It is not subscribed to the pusher channel '${name}' yet.`,
      );
    }

    return channel;
  }

  private isSubscribed(channelName: string): boolean {
    this.assertPusher(this.pusher);
    return Boolean(this.pusher.channels.channels?.[channelName]?.subscribed);
  }

  private channelNameTransformer(name: string, isPrivate = false): string {
    return isPrivate ? `private-${name}` : name;
  }

  private subscribe(channel: string): Promise<Channel> {
    return new Promise((resolve, reject) => {
      this.assertPusher(this.pusher);
      const subscription = this.pusher.subscribe(channel);

      subscription.bind("pusher:subscription_succeeded", () => {
        resolve(subscription);
      });

      subscription.bind("pusher:subscription_error", (e: unknown) => {
        reject(e);
      });
    });
  }
}
